<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  selectedDate: { type: String, required: true },
  appointmentsByDate: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['select']);
const { t, locale } = useI18n();

const cursor = ref(new Date(`${props.selectedDate}T00:00:00`));

const monthLabel = computed(() =>
  cursor.value.toLocaleDateString(locale.value.replace('_', '-'), {
    month: 'long',
    year: 'numeric',
  })
);

const weekDayLabels = computed(() => {
  const base = new Date(2024, 0, 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    return d.toLocaleDateString(locale.value.replace('_', '-'), {
      weekday: 'narrow',
    });
  });
});

const cells = computed(() => {
  const year = cursor.value.getFullYear();
  const month = cursor.value.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = first.getDay();
  const result = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(year, month, 1 - startOffset + i);
    const iso = d.toLocaleDateString('en-CA');
    result.push({
      iso,
      day: d.getDate(),
      otherMonth: d.getMonth() !== month,
      isToday: iso === new Date().toLocaleDateString('en-CA'),
      isSelected: iso === props.selectedDate,
      hasAppointments: !!props.appointmentsByDate[iso],
    });
  }
  return result;
});

const prev = () => {
  cursor.value = new Date(
    cursor.value.getFullYear(),
    cursor.value.getMonth() - 1,
    1
  );
};
const next = () => {
  cursor.value = new Date(
    cursor.value.getFullYear(),
    cursor.value.getMonth() + 1,
    1
  );
};
const select = iso => emit('select', iso);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between">
      <span class="text-sm font-semibold capitalize text-n-slate-12">{{
        monthLabel
      }}</span>
      <div class="flex gap-1">
        <button
          class="rounded p-1 text-n-slate-11 hover:bg-n-slate-3"
          :aria-label="t('PAINEL.APPOINTMENTS.MINI_CAL.PREV')"
          @click="prev"
        >
          <svg
            class="h-3 w-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button
          class="rounded p-1 text-n-slate-11 hover:bg-n-slate-3"
          :aria-label="t('PAINEL.APPOINTMENTS.MINI_CAL.NEXT')"
          @click="next"
        >
          <svg
            class="h-3 w-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
    <div
      class="grid grid-cols-7 gap-px text-center text-[10px] uppercase text-n-slate-10"
    >
      <div v-for="(d, i) in weekDayLabels" :key="i">{{ d }}</div>
    </div>
    <div class="grid grid-cols-7 gap-px">
      <button
        v-for="cell in cells"
        :key="cell.iso"
        class="relative aspect-square rounded text-xs"
        :class="[
          cell.otherMonth ? 'text-n-slate-7' : 'text-n-slate-12',
          cell.isSelected ? 'bg-n-brand font-semibold text-white' : '',
          !cell.isSelected && cell.isToday ? 'ring-1 ring-n-brand' : '',
          !cell.isSelected ? 'hover:bg-n-slate-3' : '',
        ]"
        @click="select(cell.iso)"
      >
        {{ cell.day }}
        <span
          v-if="cell.hasAppointments && !cell.isSelected"
          class="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-n-brand"
        />
      </button>
    </div>
  </div>
</template>
